import { cloneDeep, isEqual } from 'lodash';
import React, { Component } from 'react';
import { timeParse, timeFormat } from 'd3';

import { session } from '../../../services/session';
import { api } from '../../../services/api';
import { dateService } from '../../../services/date';
import analyticService from '../../../services/analytics';
import { aggregateStats, getTicks } from './graph-utils';
import megatableService from './megatable-service';

import { Loader, NoResult } from '../../misc/loader-utils';
import Graph from './graph';

import './megatable-graph.scss';

class MegatableGraph extends Component {
  constructor(props) {
    super(props);

    this.Api = api;
    this.dateService = dateService;
    this.AnalyticService = analyticService;
    this.service = megatableService;

    this.statType = 'article_graph';
    this.chartOptions = [
      {
        label: 'Clicks',
        statKey: 'clicks',
        dataType: 'number',
        d3Format: '',
      },
      {
        label: 'Revenue',
        statKey: 'total_attributed_revenue',
        dataType: 'currency',
        d3Format: '$',
      },
    ];

    if (session.isOrgAdmin && session.userRole !== 'merchants') {
      this.chartOptions.unshift({
        label: 'Earnings',
        statKey: 'publisher_total_earnings',
        dataType: 'currency',
        d3Format: '$',
      });
    }
    this.graphColors = ['green', 'purple', 'orange'];

    // Helpers
    this.parseDate = timeParse('%Y-%m-%d');
    this.formatDate = timeFormat('%Y-%m-%d');

    this.state = {
      chartOptions: session.userRole === 'merchants' ? this.chartOptions[0] : this.chartOptions[1],
      processedStats: [],
      xTicks: [], // An array of ticks, format: moment object
      loading: true,
      noResult: false,
    };
  }

  componentDidUpdate(prevProps) {
    const paramChanged = attr => !isEqual(prevProps[attr], this.props[attr]);

    if (paramChanged('stats') || paramChanged('granularity')) {
      this.setState({ loading: true });
      if (this.props.stats) {
        this.processEditStats();
      }
    }
  }

  processEditStats() {
    // Shallow copying leads to mutations to props
    let processedStats = cloneDeep(this.props.stats);
    processedStats = processedStats.map((data, index) => {
      return Object.assign(data, { color: this.graphColors[index] });
    });

    const { granularity } = this.props;
    // Backend returns all monthly stats rounded down to the 1st, replace with correct date
    if (granularity === 'monthly' || granularity === 'yearly') {
      processedStats = this.fixMonthlyStart(processedStats);
    }
    const xTicks = getTicks(granularity, this.props.dates.date_from, this.props.dates.date_to);
    if (granularity === 'weekly' || granularity === 'yearly') {
      processedStats = aggregateStats(processedStats, granularity, xTicks);
    }

    this.setState({
      processedStats,
      xTicks,
      loading: false,
      noResult: !processedStats.length,
    });
  }

  fixMonthlyStart(allStats) {
    /*
     * Replace edit start date with user-selected date_from
     * if truncated dates (YYYY-MM) match. Ex: 2019-04-01 becomes 2019-04-08
     */
    allStats.forEach((editStats, index) => {
      const formattedDateFrom = this.props.dates.date_from.slice(0, 7);
      const formattedEditStartDate = editStats.data[0].event_date.slice(0, 7);

      if (formattedDateFrom === formattedEditStartDate) {
        allStats[index].data[0].event_date = this.props.dates.date_from;
      }
    });
    return allStats;
  }

  selectChartOption(chartOption) {
    this.setState({ chartOptions: chartOption }, () => {
      const gaLabel = `${this.props.eventPrefix} Y-Axis Dimension ${chartOption.label}`;
      this.AnalyticService.trackEvent('Mega Tables', 'Click', gaLabel);
    });
  }

  render() {
    const loadingStatus = this.state.loading;
    const { noResult } = this.state;

    const chartOptions = this.chartOptions.map((chartOption, i) => {
      const selected = this.state.chartOptions.statKey === chartOption.statKey;
      return (
        <div
          key={i}
          className={`chart-btn ${selected ? 'selected' : ''}`}
          onClick={() => this.selectChartOption(chartOption)}
        >
          {chartOption.label}
        </div>
      );
    });
    const chartSelection =
      !loadingStatus && !noResult ? (
        <div className="chart-selection">
          <span>Chart:</span>
          {chartOptions}
        </div>
      ) : null;

    const graph =
      !loadingStatus && !noResult ? (
        <Graph
          stats={this.state.processedStats}
          xTicks={this.state.xTicks}
          chartOptions={this.state.chartOptions}
          dates={this.props.dates}
          granularity={this.props.granularity}
        />
      ) : null;

    return (
      <div className="megatable-graph">
        {chartSelection}
        {graph}
        <Loader loading={loadingStatus} />
        <NoResult noResult={noResult && !loadingStatus} type={this.statType} />
      </div>
    );
  }
}

export default MegatableGraph;
